/* Math Utilities for plotting, copyright 2011 (c) Craig D. Miller */

import { maxDecimalPoints } from "../../utils/constants";

/**
    @namespace Mathematics utilities useful for plotting/graphing/computing
*/

// import {Dyn} from './dynMiscellany';

export const wrapDynmathUtil = function(Dyn) {
Dyn.mathUtil = {

    // constants
    kTwoPi: 6.283185307179586476925286766559,
    kTolerance: 1e-10, // default tolerance
    kSmallerTolerance: 1e-12, // added to resolve issue with display of diagonal line equation to avoid falling for refline on y when slope is e-10
    kPrecision: 10,	 // default floating-point precision (for toPrecision(), matches tolerance)
    kStatsMaxPrecision: 15, // floating-point precision, to avoid rounding errors, see #1664, #1283
    maxFixedPrecision: 20, // fixed decimals max; Chrome will throw error if the toFixed() number of decimals exceeds 20; it has to be between 0 and 20
    maxCalcFixedDecimals: maxDecimalPoints, // fixed decimals max for calculated values, to match attribute value controls
};

// failsafe for MAX_SAFE_INTEGER AND MIN_SAFE_INTEGER are property of ES 6, not available in IE and Safari for now
Dyn.mathUtil.MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : Math.pow( 2, 53 ) - 1;
Dyn.mathUtil.MIN_SAFE_INTEGER = Number.MIN_SAFE_INTEGER ? Number.MIN_SAFE_INTEGER : -Dyn.mathUtil.MAX_SAFE_INTEGER;

/**
 * Return true if number is close to zero, within tolerance.
 * Used to avoid huge values after dividing by number.
 */
Dyn.mathUtil.notCloseToZero = function( num ) {
	"use strict";
	return( Math.abs(num) > Dyn.mathUtil.kTolerance );
};

/**
 * Check if the value is valid number or not
 */
Dyn.mathUtil.isNumber = function( value ) {
	"use strict";
	return !isNaN(parseFloat( value )) && isFinite( value );
};

/**
 * return true if the number is an integer,
 * with some tolerance to correct for roundoff error
 * @param num
 * @returns {boolean}
 */
Dyn.mathUtil.isInteger = function( num ) {
    "use strict";
    return Dyn.mathUtil.isClose( num, Math.round( num ) );
};

/**
 * Return true if number is positive, with some tolerance
 * to avoid numbers very close to zero.
 */
Dyn.mathUtil.isPositive = function( num ) {
	"use strict";
	return( num > Dyn.mathUtil.kTolerance );
};

Dyn.mathUtil.isNonNegative = function( num ) {
	"use strict";
	return( num > - Dyn.mathUtil.kTolerance );
};

Dyn.mathUtil.isEven = function( num ) {
	"use strict";
	return((num % 2) === 0 );
};

Dyn.mathUtil.isOdd = function( num ) {
	"use strict";
	return((num % 2) === 1 );
};

/**
 * Invert the given value (return 1/n), but allow zero values to return zero,
 * avoiding a divide-by-zero error.
 * @param num {Number}
 */
Dyn.mathUtil.invertSafely = function( num ) {
	"use strict";
	return (num !== 0) ? (1/num) : 0;
};

/**
 * Round floating-point number to an integer if it is very close to one,
 * to eliminate roundoff error from calculations.
 */
Dyn.mathUtil.roundIfInteger = function( num ) {
	"use strict";
	var i = Math.round( num );

	if( Dyn.mathUtil.isClose( num, i )) {
		return i;
	} else {
		return num;
	}
};

/**
 * Round a numeric value to the given number of significant digits, and remove trailing zeros
 * roundToPrecision( 1234.5678, 2 ): 1200
 * roundToPrecision( 20.1237, 3 ): 20.1
 * roundToPrecision( 20.1237, 5 ): 20.124
 * roundToPrecision( 20.1234, 2 ): 20
 * roundToPrecision( 20.1000, 2 ): 20
 * roundToPrecision( 20.0000, 2 ): 20
 * See also roundToDecimalPlaces()
 * @param num
 * @param [optionalPrecision]
 * @returns {Number}
 */
Dyn.mathUtil.roundToPrecision = function( num, optionalPrecision ) {
	"use strict";
	var precision = optionalPrecision || Dyn.mathUtil.kPrecision;
    Dyn.assert( precision >= 1 && precision <= 100 );
    Dyn.assertFiniteNumeric( num );
	return parseFloat( num.toPrecision( precision ));
};

/**
 * Round a numeric value to the given number of decimal places after zero
 * roundToPrecision( 1234.5678, 2 ): 1234.57
 * roundToDecimalPlaces( 20.1237, 3 ): 20.124
 * roundToDecimalPlaces( 20.1237, 0 ): 20
 * roundToDecimalPlaces( 20.1000, 2, false ): 20.1
 * roundToDecimalPlaces( 20.1000, 2, true ): "20.10"
 * See also roundToPrecision()
 * @param num {number}
 * @param [optionalDecimalPlaces] -- 0+
 * @param [hasTrailingZeros] - if true, return a string with fixed number of decimals, including trailing zeros
 * @returns {Number|String} - rounded number, or string if hasTrailingZeros===true
 */
Dyn.mathUtil.roundToDecimalPlaces = function( num, optionalDecimalPlaces, hasTrailingZeros=false ) {
    var decimals = this.clipToMinMax( (optionalDecimalPlaces || 0), 0, Dyn.mathUtil.maxFixedPrecision);
    return hasTrailingZeros ? num.toFixed( decimals ) : parseFloat( num.toFixed( decimals ));
};

/**
 * Round calculated numeric values to fix common javascript floating point errors, before display by user.
 * This is similar to roundToPrecision(), but allows for NaN values.
 * These errors tend to crop up in the 17th decimal place, so we round to 15 floating decimal places.
 *      Needed because the number of decimal places displayed can vary (because of attribute and
 * user-specified precision settings), AND javascript math can make errors at about the 17th decimal
 * place, pre-round our statistics values to 15 digits, to avoid off-by-one rounding errors.
 *      Example: value shows as 0.167 (error), instead of 0.168 (correct), because 0.16749999999999999
 * was the calculated result, instead of the true result of 0.1675 which rounds up to 0.168.
 * @param num
 * @returns {number}
 */
Dyn.mathUtil.fixPrecision = function( num ) {
    return Number.isFinite( num ) ? parseFloat( num.toPrecision( Dyn.mathUtil.kStatsMaxPrecision )) : Number.NaN;
}

/**
 * Add comma separators to the numeric digits in the string.
 * @param numericString
 * @returns {string}
 */
Dyn.mathUtil.numericStringWithCommas = function( numericString ) {
    "use strict";
    var parts = (numericString+"").split("."); // Note: numericString+"" converts to string, better than .toString() because it works for Null and undefined.
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
};

/**
 Round the given value to some precision, based on the nearest power
 of 10 to the scale value.  If given an options object,
 returns the precision (>=0) to use for formatting as a string.

 Examples:
 (inputs)			(intermediates)				(results)
 Value		Scale	Log10-Scale	Rounded-Scale	Rounded-Value	Precision
 1234.5		120		2			100				1200			0
 1.234		.4		-1			.1				1.2				1
 1.234		.016	-2			0.01			1.22			2

 Used to find the nearest nice data value within one pixel of the mouse,
 by passing the data-values-per-pixel amount as the scale.
 * @param num - number to round
 * @param scale - scale value to determine precision of rounding
 * @param {*} [options] - optional precision parameters, and final precision output
 * @param {number} [options.precision] - +/- precision value as input, and final precision as output
 * @param {number} [options.min] - minimum precision, e.g. 0 for integer, 2 for 2 decimals
 * @param {number} [options.max] - minimum precision, e.g. 0 for integer, 2 for 2 decimals
 * @returns {number} -- rounded value
 */
Dyn.mathUtil.roundToScale = function( num, scale, options ) {
	"use strict";
	// get divisor that shifts scale into range [1.0 - 10.0)
	let precision = - Math.round( Dyn.mathUtil.log10( Math.abs( scale )));
	if( options ) {
	    if( Number.isInteger( options.precision )){ // if non-zero precision
            precision += options.precision  // adjust scale by precision (e.g. +2 adds 2 decimals of precision)
        }
	    if( Number.isInteger( options.min )){ // minimum precision, e.g. num=123.456, precision=2, min=1, rounds to 123.4 instead of 120.
            precision = Math.max(options.min, precision);
        }
        if( Number.isInteger( options.max )) { // maximum precision, e.g. 10 = no more than 10 decimal places, can be negative.
            precision = Math.min( options.max, precision );
        }
    }
	let divisor = Math.pow( 10.0, - precision );

	// save precision if we are given pointer, for formatting
	// e.g. Log10 of scale 0.01 is -2, so we return precision of 2,
	if ( options ) {
		options.precision = (precision > 0) ? precision : 0;
	}

	// round our value at same power of ten as scale
	return( divisor * Math.round( num / divisor ));
};

/**
 * Calculate the decimal points of the number
 * it will be used to set the decimal place for the attribute default
 * @param num {number} -- any valid finite number
 * @returns {number} -- integer 0+
 */
Dyn.mathUtil.decimalPlaces = function( num ) {
	"use strict";
	Dyn.assert( typeof num === "number" && isFinite( num ));
	var match = (""+num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
	if (!match) { return 0; }
	return Math.max(
		0,
		// digits count on right of decimal point.
		// adjust in case of scientific notation.
		(match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
};

/**
 * Calculate the minimum interval width with the given number of decimal places.
 * E.g. 0=>1, 1=>0.1, 2=>0.01, 3=>0.001, etc.
 * @param num -- 0+ integer returned from Dyn.mathUtil.decimalPlaces()
 * @returns {number} -- interval width <= 1
 */
Dyn.mathUtil.decimalPlacesToIntervalWidth = function( num ) {
    "use strict";
    return Math.pow( 10, -(num));
}

/**
 * Is the value a finite number? Unlike the global "isFinite", returns
 * false for (null || undefined || "").  Strings or Booleans are converted
 * to Numbers in the usual way.
 * @param val
 * @returns {Boolean} true if the value is finite.
 */
Dyn.mathUtil.isFinite = function( val ) {
	"use strict";
	// NOTE: identical to Number.isFinite() EXCEPT that...
    // Number.isFinite("1") => false
    // Dyn.mathUtil.isFinite("1") => true
    // TODO: consider replacing Dyn.mathUtil.isFinite() with Number.isFinite, but check for this difference where it matters. --Craig 2019/12/12
	return( val!==null && val!==undefined && val!=="" && isFinite( val ));
};

/**
 * Are the two numbers close, within the tolerance?
 * @returns {boolean}
 */
Dyn.mathUtil.isClose = function( num1, num2, optionalTolerance ) {
	"use strict";
	var t = optionalTolerance || Dyn.mathUtil.kTolerance;
	return( Math.abs( num1 - num2 ) < t );
};

Dyn.mathUtil.isLessThanOrEqual = function( num1, num2 ) {
    return num1 <= (num2+Dyn.mathUtil.kTolerance); // less than or close to equal?
};
Dyn.mathUtil.isGreaterThanOrEqual = function( num1, num2 ) {
    return num1 >= (num2-Dyn.mathUtil.kTolerance); // greater than or close to equal?
};
Dyn.mathUtil.isLessThan = function( num1, num2 ) {
    return num1 < (num2-Dyn.mathUtil.kTolerance); // less than but not close to equal?
};
Dyn.mathUtil.isGreaterThan = function( num1, num2 ) {
    return num1 > (num2+Dyn.mathUtil.kTolerance); // greater than but not close to equal?
};

/**
 * Round floating-point number downward (note: this is different from
 * rounding towards zero; negative numbers increase in magnitude).
 */
Dyn.mathUtil.floor = function( num ) {
	"use strict";
	return Math.floor( num + Dyn.mathUtil.kTolerance );
};

/**
 * Round floating-point number upward (note: this is different from
 * rounding away from zero; negative numbers decrease in magnitude).
 */
Dyn.mathUtil.ceiling = function( num ) {
	"use strict";
	return( Math.ceil( num - Dyn.mathUtil.kTolerance ));
};

/**
 * Get modulo (remainder of num / divisor), a
 * and  fix roundoff errors introduced by % operator.
 */
Dyn.mathUtil.modulo = function( num, divisor ) {
    "use strict";
    return( this.roundToPrecision( num % divisor ));
};

/**
 * Clip the number so it is not lower than min, or larger than max.
 */
Dyn.mathUtil.clipToMinMax = function( min, max, num ) {
	"use strict";
	return( Math.max( min, Math.min( max, num )));
};

/**
 * Convert value to positive zero if it is negative zero.
 */
Dyn.mathUtil.removeNegativeZero = function( num ) {
	"use strict";
	return(( num === -0 ) ? 0 : num );
};

class DynRect {
    /**
     * Rect : Constructor for a view rectangle
     * @param [iLeftOrRect] {Number}
     * @param [iTop] {Number}
     * @param [iWidth]  {Number}
     * @param [iHeight] {Number}
     * @constructor
     */
    constructor( iLeftOrRect, iTop, iWidth, iHeight ) {
        if( typeof iLeftOrRect === "object" && iLeftOrRect !== null ) {
            this.copyRect( iLeftOrRect );
        } else {
            this.x = iLeftOrRect || 0;
            this.y = iTop || 0;
            this.width = iWidth || 0;
            this.height = iHeight || 0;
        }
    }

    left() {
        return this.x;
    }
    top() {
        return this.y;
    }
	right() {
		return this.x + this.width;
	}
	bottom() {
		return this.y + this.height;
	}
	centerX() {
		return this.x + this.width/2;
	}
	centerY() {
		return this.y + this.height/2;
	}
	leftOrBottom( wantHorizontal ) {
		return( wantHorizontal ? this.x : this.y + this.height );
	}
	rightOrTop( wantHorizontal ) {
		return( wantHorizontal ? this.x + this.width : this.y );
	}
	rightOrBottom( wantHorizontal ) {
		return( wantHorizontal ? this.x + this.width : this.y + this.height );
	}
	widthOrHeight( wantHorizontal ) {
		return( wantHorizontal ? this.width : this.height );
	}

	copyRect( otherRect ) {
		this.x = otherRect.x;
		this.y = otherRect.y;
		this.width = otherRect.width;
		this.height = otherRect.height;
		return this; // for chaining
	}
    equals( otherRect ) {
        return( this.x === otherRect.x &&
        this.y === otherRect.y &&
        this.width === otherRect.width &&
        this.height === otherRect.height );
    }
	normalize() {
		if( this.width < 0 ) {
			this.x += this.width;
			this.width = -(this.width);
		}
		if( this.height < 0 ) {
			this.y += this.height;
			this.height = -(this.height);
		}
        return this; // for chaining
	}
	union( rect ) {
		var left = Math.min( this.left(), rect.left()),
			top = Math.min( this.top(), rect.top()),
			right = Math.max( this.right(), rect.right()),
			bottom = Math.max( this.bottom(), rect.bottom());
		this.x = left;
		this.y = top;
		this.width = right - left;
		this.height = bottom - top;
        return this; // for chaining
	}
    offset( xOffset, yOffset ) {
	    this.x += xOffset;
	    this.y += yOffset;
	    return this; // for chaining
    }
    expand( dx, dy ) { // expand or shrink the rectangle
        this.x -= dx;
        this.y -= dy;
        this.width  = Math.max(0, this.width  + 2*dx );
        this.height = Math.max(0, this.height + 2*dy );
        return this; // for chaining
    }
    addPoint( x, y ) { // add point to rectangle
        var left = Math.min( this.left(), x ),
            top = Math.min( this.top(), y ),
            right = Math.max( this.right(), x ),
            bottom = Math.max( this.bottom(), y );
        this.x = left;
        this.y = top;
        this.width = right - left;
        this.height = bottom - top;
        return this; // for chaining
    }
    setFromTwoPoints( p1, p2 ) {
        this.x = p1.x;
        this.y = p1.y;
        this.width = p2.x - p1.x;
        this.height = p2.y - p1.y;
        this.normalize();
        return this; // for chaining
    }
	addDebugRect( paper, optionalStroke, optionalFill ) {
        Dyn.plotUtil.addDebugRect( paper, this, this, optionalStroke, optionalFill );
	}
	removeDebugRect() {
        Dyn.plotUtil.removeDebugRect( this );
	}
	toString() {
		return "(x:"+this.x+",y:"+this.y+",width:"+this.width+",height:"+this.height+"}";
	}

    /** is the (x,y) point within the rectangle */
    isPointInRect( x, y ) {
	    return (( this.width > 0 && this.height > 0) && (x >= this.left()) && (x <= this.right()) && (y >= this.top()) && (y <= this.bottom()));
    }

    /** is the other rectangle overlapping with this rectangle. True if rectangles have any points in common */
    isOverlappingRect( otherRect ) {
        return (
            ( this.width > 0 && this.height > 0) &&
            ! (
                (otherRect.right() <= this.left()) ||
                (otherRect.left() >= this.right()) ||
                (otherRect.bottom() <= this.top()) ||
                (otherRect.top() >= this.bottom())
            )
        );
    }

} // class DynRect
Dyn.mathUtil.Rect = DynRect;

/** normalize a rectangle-like object (see also Dyn.mathUtil.Rect.normalize()) */
Dyn.mathUtil.normalizeRect = function( r ) {
	"use strict";
	if( r.width < 0 ) {
		r.x += r.width;
		r.width = -(r.width);
	}
	if( r.height < 0 ) {
		r.y += r.height;
		r.height = -(r.height);
	}
};
Dyn.mathUtil.copyRect = function( r, otherRect ) {
    "use strict";
    r.x = otherRect.x;
    r.y = otherRect.y;
    r.width = otherRect.width;
    r.height = otherRect.height;
    return r; // for chaining
};

class DynRange {
    /**
     * Range : construct a numeric range
     * @param iMinOrRange {Number}
     * @param iMax {Number}
     * @constructor
     */
    constructor( iMinOrRange, iMax ) {
        if( typeof iMinOrRange === "object" && ! Number.isFinite(iMax) ) {
            this.min = iMinOrRange.min;
            this.max = iMinOrRange.max;
        } else {
            this.min = iMinOrRange;
            this.max = iMax;
        }
    }

    normalize() { // ensure min <= max
		var tmp;
		if( this.max < this.min ) {
			tmp=this.min; this.min = this.max; this.max = tmp;  // swap
		}
		return this; // for chaining
	}
	makeNonZeroWidth( optionalRange ) { // ensure min != max
		//var equalOffset = 1;
		if( ! Dyn.mathUtil.isFinite( this.min )) {
			this.min = 0;	// if invalid, create a false, arbitrary scale
			this.max = 10;
		}
		if( this.min === this.max ){
			// if equal, create a false, non-zero range, default to +/-1 unit
			//  Note: it might be nicer to examine min and offset based on scale of number
            let equalOffset = (Dyn.mathUtil.isFinite( optionalRange ) && Dyn.mathUtil.isPositive( optionalRange)) ?
                    optionalRange * 0.5 : 1;
			this.min = this.min - equalOffset;
			this.max = this.max + equalOffset;
		}
        return this; // for chaining
	}
    addPoint( p ) {
	    if( p < this.min ) {
	        this.min = p;
        }
        if( p > this.max ) {
	        this.max = p;
        }
    }
	width() {
		return this.max - this.min;
	}
    midPoint() {
	    return (this.min + this.max ) / 2;
    }
	set( iMin, iMax ) {
		this.min = iMin;
		this.max = iMax;
	}
	isInRange( iValue ) {
		return iValue >= this.min && iValue <= this.max;
	}
	toString() {
		return "(min:"+this.min+",max:"+this.max+"}";
	}
    isFinite() {
        return isFinite( this.min ) && isFinite( this.max );
    }
    equals( otherRange ) {
        return( this.min === otherRange.min && this.max === otherRange.max );
    }

} // class DynRange
Dyn.mathUtil.Range = DynRange;

/**
 * @param r1 {Dyn.mathUtil.range}
 * @param r2 {Dyn.mathUtil.range}
 * @returns {boolean}
 */
Dyn.mathUtil.equalRanges = function( r1, r2 ) {
	"use strict";
	var r1Exists = Boolean( r1 ),
		r2Exists = Boolean( r2 );
	if( r1Exists !== r2Exists ) {
		return false; // one is undefined/null and the other is not
	}
	if( r1Exists ) {
		return( r1.min === r2.min && r1.max === r2.max ); // values equal or not
	}
	return true; // both are null objects
};

class DynCoordMap {
    /**
     * CoordMap : holds a one dimensional, linear coordinate transformation and it's inverse.
     * In other words, if a value is in a range [A-B], we map it to the range [C-D]
     * with Transform( value ). Handles zero-width ranges, by mapping all values
     * to the midrange (because it looks good in a plot).
     * @param outMin  {Number}
     * @param outMax {Number}
     * @param inMin {Number}
     * @param inMax {Number}
     * @param [iBinWidth]  {Number | undefined}
     * @constructor
     */
    constructor( outMin, outMax, inMin, inMax, iBinWidth ) {
        "use strict";
        this.inRange = inMax - inMin;		// incoming data range
        this.outRange = outMax - outMin;		// outgoing range
        this.inTranslation = -inMin;			// added before scale
        this.scale = 0;							// multiplier for transformation
        this.outTranslation = outMin;			// added after scale
        this.outFractionTranslation = 0;		// used for axis ticks, etc.
        this.binWidth = iBinWidth || 0;  	// zero for continuous axes, positive for binned axes

        if( Dyn.mathUtil.notCloseToZero( this.inRange ) )	// if normal range
        {
            this.scale = this.outRange / this.inRange;
            this.outFractionTranslation = this.outTranslation;
        } else {
            // handle an incoming range of zero by mapping all values to the center of the outgoing range.
            this.scale = 0.0;
            this.outTranslation = outMin + this.outRange / 2;
            this.outFractionTranslation = outMin;
        }
    }

	toString() {
		return "(inRange:"+this.inRange+",outRange:"+this.outRange+",inTranslation:"+this.inTranslation+",scale:"+this.scale+",outTranslation:"+this.outTranslation+"}";
	}

    clone() {
        return new DynCoordMap(
            this.outTranslation, this.outRange + this.outTranslation,
            - this.inTranslation, this.inRange - this.inTranslation,
            this.binWidth );
    }

	/**
	 * Transform a data value in the incoming range to the outgoing range (a.k.a. source to destination)
	 * @param dataValue {Number}
	 * @returns {Number}
	 */
	transform( dataValue ) {
		return ((dataValue + this.inTranslation) * this.scale) + this.outTranslation;
	}

	/**
	 * Transform a data value from the outgoing range to the incoming range  (a.k.a. destination to source)
	 * @param dataValue {Number}
	 * @returns {Number}
	 */
	inverseTransform( dataValue ) {
		return (this.scale===0) ? 0 : ((dataValue - this.outTranslation) / this.scale) - this.inTranslation;
	}

	/**
	 * Transform a fraction [0.0-1.0] to the outgoing (destination) range, used for axes ticks, etc.
	 * @param fraction {Number}
	 * @returns {Number}
	 */
	fractionToDest( fraction ) {
		return (fraction * this.outRange) + this.outFractionTranslation;
	}

	/**
	 * Transform the outgoing (destination) value to a fraction [0.0-1.0]
	 * @param dataValue {Number}
	 * @returns {Number}
	 */
	destToFraction( dataValue ) {
		return (this.outRange===0) ? 0 : (dataValue - this.outFractionTranslation) / this.outRange ;
	}

	/**
	 * Transform a fraction [0.0-1.0] to the incoming (source) range, used for axes ticks, etc.
	 * @param fraction {Number}
	 * @returns {Number}
	 */
	fractionToSource( fraction ) {
		return (fraction * this.inRange) - this.inTranslation;
	}

	/**
	 * Transform the incoming (source) value to a fraction [0.0-1.0]
	 * @param dataValue {Number}
	 * @returns {Number}
	 */
	sourceToFraction(  dataValue ) {
		return (this.inRange===0) ? 0 : (dataValue + this.inTranslation) / this.inRange ;
	}

	/**
     * Transform a length in the destination coordinates to a length in the source coordinates.
     * NOTE: was mis-named destToSourceAbs(), prior to 2.4.0-13 */
	scaleAbs( dataValue ) {
		return Math.abs( this.scale * dataValue );
	}
    signedLength( dataValue ) {
        return( this.scale * dataValue );
    }

    /** Transform a length in the source coordinates to a length in the destination coordinates.
     * NOTE: was mis-named sourceToDestAbs(), prior to 2.4.0-13 */
    inverseScaleAbs( dataValue ) {
        "use strict";
        return Math.abs( dataValue / this.scale ); // constructor ensures scale!===0
    }
    inverseSignedLength( dataValue ) {
        return( dataValue / this.scale ); // constructor ensures scale!===0
    }

} // class DynCoordMap
Dyn.mathUtil.CoordMap = DynCoordMap;

/**
 *  Clip to the integer range which EXCLUDES the upper value.
 *  Assumes that all values are integers.
 *  @returns integer in [iMin - iMax) range.
 */
Dyn.mathUtil.clipToIntegerRange = function( iInteger, iMin, iMax ) {
	"use strict";
	Dyn.assert( iInteger === Math.round( iInteger ));
	if( iInteger < iMin ){
		return iMin;
	} else if( iInteger >= iMax ) {
		return iMax-1;
	}
	return iInteger;
};

/**
 *  Test for inside integer range which EXCLUDES the upper value.
 *  Assumes that all values are integers.
 *  @returns {boolean}
 */
Dyn.mathUtil.isInIntegerRange = function( iInteger, iMin, iMax ) {
	"use strict";
	Dyn.assert( iInteger === Math.round( iInteger ));
	return( iMin <= iInteger && iInteger < iMax );
};

/**
 *  Clip to the real numeric range which INCLUDES the upper value.
 *  Assumes that all values are finite real numbers.
 *  @returns {number} in [iMin - iMax] range.
 */
Dyn.mathUtil.clipToRange = function( iNumber, iMin, iMax ) {
	"use strict";
	Dyn.assert( typeof iNumber === "number" && isFinite( iNumber ));
	if( iNumber < iMin ){
		return iMin;
	} else if( iNumber > iMax ) {
		return iMax;
	}
	return iNumber;
};

/**
 * Test for inside the real numeric range which INCLUDES the upper value.
 *  Assumes that all values are finite real numbers.
 *  @returns {boolean} true if value is in the range
 */
Dyn.mathUtil.isInRange = function( iNumber, iMin, iMax ) {
	"use strict";
	Dyn.assert( typeof iNumber === "number" && isFinite( iNumber ));
	return( iMin <= iNumber && iNumber <= iMax );
};

/**
 * Distance between two points.
 * @param {{x,y}} iPoint1
 * @param {{x,y}} iPoint2
 */
Dyn.mathUtil.distance = function( iPoint1, iPoint2 ) {
	"use strict";
	var dx = iPoint1.x - iPoint2.x,
		dy = iPoint1.y - iPoint2.y;
	return Math.sqrt( dx*dx + dy*dy );
};

/**
 * Returns the counterclockwise angle in radians (not degrees) between the positive X axis and the point (x, y).
 * E.g. 3 o'clock is 0, 12 o'clock is PI/2, 6 o'clock is -PI/2.
 * @param x {number}
 * @param y {number}
 * @return {number} angle, between PI and -PI radians.
 */
Dyn.mathUtil.angle = function( x, y ) {
    "use strict";
    return Math.atan2( y, x ); // note y first
};

/**
 * Convert Polar (r,a) to Cartesian (x,y) coordinates.
 * To convert to and from polar coordinates use:
 *      [x,y] === [polarToX(r,a),polarToY(r,a)]
 *      [r,a] === [distance(x,y),angle(x,y)]   (note: angle() is in range [-pi, pi], but 'a' is not restricted to this range)
 * Reference: https://en.wikipedia.org/wiki/Polar_coordinate_system#Converting_between_polar_and_Cartesian_coordinates
 * @param radiusLength
 * @param angleRadians
 * @returns {number}
 */
Dyn.mathUtil.polarToX = function( radiusLength, angleRadians ) {
    "use strict";
    return radiusLength * Math.cos( angleRadians );
};
Dyn.mathUtil.polarToY = function( radiusLength, angleRadians ) {
    "use strict";
    return radiusLength * Math.sin( angleRadians );
};

/**
 * Parse the value, string, int, float
 * if its number in string format, it returns
 * appropriate format
 */
Dyn.mathUtil.parseNumber = function( input ) {
	"use strict";
    var m = (/[\d]+(\.[\d]+)?/).exec( input );
    if ( m ) {
       // Check if there is a decimal place
       if ( m[1] ) {
            return parseFloat( input );
        } else {
            return parseInt( input );
        }
    }
    return input;
};

/**
 * Compute the median of an array of finite numeric values.
 * Warning, the input array is sorted, on the assumption that it is most
 * computationally efficient to directly modify a temporary array.  Caller
 * should make a copy of the array if this is undesired behavior.
 * @param ioArray array of numbers (will be sorted ascending)
 * @returns {Number} median value or undefined if ioArray.length===0
 */
Dyn.mathUtil.medianOfNumericArray = function( ioArray ) {
	"use strict";

	function lessThan(a,b) { return a-b; } // for ascending numeric sort()

	function median( iSortedArray ) {
		var i = (iSortedArray.length - 1)/ 2, // middle index in 0-(n-1) array
			i1 = Math.floor(i),
			i2 = Math.ceil(i);
		if( i < 0 ) {
			return NaN; // length === 0
		} else if( i===i1 ) {
			return iSortedArray[i];
		} else {
			return (iSortedArray[i1]+iSortedArray[i2]) / 2;
		}
	}

	ioArray.sort( lessThan );
	return median( ioArray );
};

// default for quartilesOfNumericArray()
// if true default to exclusive method, if false, default to inclusive method.
Dyn.mathUtil.useExcludeMedianMethodForQuartiles = false;

/**
 * Compute the quartiles of an array of finite numeric values.
 *
 * Warning, the input array is sorted, on the assumption that it is most
 * computationally efficient to directly modify a temporary array.  Caller
 * should make a copy of the array if this is undesired behavior.
 *
 * Note: The definition of a quantile index that does not exactly correspond to a
 * sorted cases index is a range, not a value, so there are various methods to compute
 * an exact value, all involving some combination of the value above and below the quantile location.
 * See: https://en.wikipedia.org/wiki/Quantile#Estimating_quantiles_from_a_sample, methods R6 and R7.
 * Here we match Excel to use the "exclusive" method (e.g. =quartile.exc( range, 0.25 )) by default,
 * but have the option to use the "inclusive" method (e.g. =quartile.inc( range, 0.25 )).
 * (Excel percentile.inc() and percentile.exc() use the same formulas).
 * See Dyn.mathUtil.useExcludeMedianMethodForQuartiles for the default setting.
 *
 * @param ioArray -- array of numeric values to compute quartiles from (will be sorted ascending)
 * @param calcWhiskers -- calculate boxplot whiskers to use for a boxplot with outliers?
 * @param useInclusiveMethod -- optional; if true or false, override the default, otherwise follow useExcludeMedianMethodForQuartiles
 * @returns {{}}
 */
Dyn.mathUtil.quartilesOfNumericArray = function( ioArray, calcWhiskers, useInclusiveMethod ) {

    let quartiles = {},
        useExclusiveMethod = ((typeof useInclusiveMethod==='boolean') ? (!useInclusiveMethod) : Dyn.mathUtil.useExcludeMedianMethodForQuartiles );

    function lessThan(a, b) {
        return a - b;
    } // for ascending numeric sort()

    function quantile(iSortedArray, value /* [0-1] */, useExclusiveMethod) {
        let i = (useExclusiveMethod ?
            ((iSortedArray.length + 1) * value) - 1 : // EXCLUSIVE: fractional index in 0-(n-1) array
            (iSortedArray.length - 1) * value), // INCLUSIVE version: fractional index in 0-(n-1) array
            i1 = Math.floor(i),
            i2 = Math.ceil(i),
            f1 = i2 - i; // fraction of i1 to use if i!===i1
        Dyn.assert(i1 === Dyn.mathUtil.floor(i));
        Dyn.assert(i2 === Dyn.mathUtil.ceiling(i));
        if( i < 0 ){
            return NaN; // length === 0
        } else if( i === i1 ){
            return iSortedArray[i];
        } else {
            // console.log("quartilesOfNumericArray", i, i1, i2, iSortedArray[i1], iSortedArray[i2],
            //     "mid=", (iSortedArray[i1] + iSortedArray[i2]) / 2,
            //     "interpolate", useExclusiveMethod ? "exclusive" : "inclusive", (iSortedArray[i1] * (f1) + iSortedArray[i2] * (1 - f1))
            // );
            return iSortedArray[i1] * (f1) + iSortedArray[i2] * (1 - f1);
        }
    }

    function findWhisker1DataValue(iSortedArray, q1, IQR) {
        // compute the whisker numeric location
        let computedWhisker1 = q1 - (1.5 * IQR);
        // from low to high values, find the first data point >= computedWhisker1
        let dataWhisker1 = ioArray.find(v => Dyn.mathUtil.isGreaterThanOrEqual(v, computedWhisker1));
        if( (!Number.isFinite(dataWhisker1)) || Dyn.mathUtil.isGreaterThanOrEqual(dataWhisker1, q1) ){
            dataWhisker1 = q1; // whisker1 === q1 if no data value < q1 and > computedWhisker1
        }
        return dataWhisker1;
    }

    function findWhisker2DataValue(iSortedArray, q3, IQR) {
        // compute the whisker numeric location
        let computedWhisker2 = q3 + (1.5 * IQR),
            dataWhisker2;
        // from high to low values, find the first data point <= computedWhisker1 and >= q1
        for( let i = ioArray.length - 1; i >= 0; i-- ){
            let d = ioArray[i];
            if( Dyn.mathUtil.isLessThanOrEqual(d, q3) ){
                dataWhisker2 = q3; // whisker2 === q3 if no data value > q3 and < computedWhisker2
                break;
            } else if( Dyn.mathUtil.isLessThanOrEqual(d, computedWhisker2) ){
                dataWhisker2 = d;
                break;
            }
        }
        return dataWhisker2;
    }

    ioArray.sort(lessThan);
    quartiles.median = quantile(ioArray, 0.5 ); // inclusive/exclusive methods identical
    quartiles.q1 = quantile(ioArray, 0.25, useExclusiveMethod);
    quartiles.q3 = quantile(ioArray, 0.75, useExclusiveMethod);
    quartiles.min = quantile(ioArray, 0); // inclusive/exclusive methods identical
    quartiles.max = quantile(ioArray, 1); // inclusive/exclusive methods identical
    quartiles.IQR = quartiles.q3 - quartiles.q1;
    if( calcWhiskers ){
        quartiles.whisker1 = findWhisker1DataValue(ioArray, quartiles.q1, quartiles.IQR);
        quartiles.whisker2 = findWhisker2DataValue(ioArray, quartiles.q3, quartiles.IQR);
    }
	return quartiles;
};

/**
 * Get sum of the array
 */
Dyn.mathUtil.sumOfNumericArray = function( array ) {
    return( array.length ? array.reduce( (a,b) => a+b ) : NaN );
};

/**
 * Get mean of an array of finite numeric values.
 * Note that sample mean and population mean have the same formula.
 * @param array
 * @returns {number} -- sum or NaN if array length zero
 */
Dyn.mathUtil.meanOfNumericArray = function( array ) {
	return( array.length ? Dyn.mathUtil.sumOfNumericArray( array ) / array.length: NaN );
};

    /*
     * Get the Mean Average Deviation of an array of finite numeric values.
     * @param array -- array of numeric data values
     * @param [optionalMean] -- pre-calculated mean if we have it, for efficiency
     * @returns {number}
     */
    Dyn.mathUtil.meanAvDevOfNumericArray = function( array, optionalMean ) {
        "use strict";
        // Get the average
        let mad = NaN;
        if( array.length > 0 ) {
            // Mean Average deviation = (sum of abs( v - mean )) / n
            let mean = optionalMean || Dyn.mathUtil.meanOfNumericArray ( array ),
                sum = array.reduce( (prev,curr) => (prev + Math.abs( curr - mean )), 0 );
            mad = sum / array.length;
        }
        return mad;
    };

/*
 * Get the Variance of an array of finite numeric values.
 * Variance is the average of the squared difference from the Mean
 * divide by n-1 for Variance of a sample (default)
 * divide by n for variance of a population
 * @param array -- array of numeric data values
 * @param isPopulation {boolean} -- if true get population variance, otherwise get sample variance
 * @returns {number}
 */
Dyn.mathUtil.varianceOfNumericArray = function( array, isPopulation /* = false, optional */) {
	"use strict";
	// Get the average
	let mean = Dyn.mathUtil.meanOfNumericArray ( array );

	// squared difference of the array from mean
	let squareDiffs = array.map(function(value){
		var diff = value - mean;
		return Math.pow( diff, 2 );
	});
	let divisor = array.length - (Boolean(isPopulation )? 0 : 1),
        variance = NaN;
    if( divisor > 0 ) {
        // average of squared difference
        variance = Dyn.mathUtil.sumOfNumericArray( squareDiffs ) / divisor;
    }
	return variance;
};

/**
 * Get the sample or population standard deviation (St. Dev.) of an array of finite numeric values.
 * @param array -- array of numeric data values
 * @param isPopulation {boolean} -- if true get population s.d., otherwise get sample s.d.
 * @returns {number}
 */
Dyn.mathUtil.stDevOfNumericArray = function( array, isPopulation /* = false, optional */) {
	"use strict";
	return Math.sqrt( Dyn.mathUtil.varianceOfNumericArray( array, isPopulation ) );
};

/**
 * Get the standard error (Std. Err.) of an array of finite numeric values.
 * @param array -- array of numeric data values
 * @returns {number}
 */
Dyn.mathUtil.stdErrorOfNumericArray = function( array ) {
    let stdError = NaN;
    if( array.length > 0 ) {
        let sqrtN = Math.sqrt( array.length ),
            sampleStdDev = Math.sqrt( Dyn.mathUtil.varianceOfNumericArray( array, false ));
        stdError = sampleStdDev / sqrtN;
    }
    return stdError;
};

/*
 * Get mode or modes[] of an array of values.
 * Usually these are finite numeric values, but strings are OK too.
 * METHODS:
 * 	1.	mode of empty array is [] -- no mode.
 * 	2.  mode of single value array [x] is [x] -- one mode.
 * 	3.  mode of array with 2+ items and no duplicate values is [] -- no mode.
 * 	4.  mode of array with duplicates has those values with highest count,
 * 			e.g. mode of 4,2,2,2,29,29,29 is [2,29] -- two modes.
 *
 * @param ioArray - array of values (numeric, non-numeric, missing, etc).
 * @returns {Array} - array of values that are the mode (0+), having an equal count.
 */
Dyn.mathUtil.modeOfNumericArray = function( ioArray ) {
	"use strict";
	var valueToCountMap = {},
		modes = [],
		maxCount = 0,
		itemCount;

	// scan values and accumulate counts
	ioArray.forEach( function(item) {
		if( valueToCountMap[item] ) {
			valueToCountMap[item]++;
		} else {
			valueToCountMap[item] = 1;
		}
		itemCount = valueToCountMap[item];
		if( itemCount > maxCount ) {
			modes = [ item ];  // new mode array
			maxCount = valueToCountMap[item];
		} else if( itemCount === maxCount ) {
			modes.push( item ); // add to mode array (multi-modal)
		}
	});

	// get an array of all values with the maximum count (0+ mode values), only if count > 1.
	if( ! (maxCount > 1 || ioArray.length === 1 )) {
		modes = []; // no mode if all values are unique, and and 2+ values.
	}

	return modes;
};

/**
 * Log base 10
 * @param num
 * @returns {number}
 */
Dyn.mathUtil.log10 = function( num ) {
	"use strict";
	return( Math.log( num ) / Math.LN10 );
};

// compute the angle in Radians formed by 3 points, with point P2 at the crux of the angle
Dyn.mathUtil.angleBetweenPoints = function( P1, P2, P3 ) {
    return(
        Math.atan2(P3.y - P1.y, P3.x - P1.x) -
        Math.atan2(P2.y - P1.y, P2.x - P1.x));
};

class slopeInterceptDecimalPlaces {
    /**
     * Compute the significant digits for Slope, Intercept, R, R-squared.
     * Unlike findNeededFractionDigits(), we do not attempt to compute the number of digits
     * to display to perfectly re-create the line equation at the current screen resolution.
     * The user may have to increase the precision of slope or intercept for this.
     * (e.g. slope = 0.123 if rounded to significant digits, but slope = 0.123456 to perfectly
     * re-create the Least Squares line with an f(x) equation.)
     * @param xAttrib
     * @param yAttrib
     * @returns {{rValueDigits: *, interceptDigits: *, slopeDigits: *}}
     */
    constructor( xAttrib, yAttrib) {
        const userPref = Dyn.preferences.getPrecision(),
            slopeSigFigDelta = 1, // +1 for slope/intercept, from "Significant Figures - Rules & in Tuva" from Kristian Hunter-Thomson
            interceptSigFigDelta = 1,
            rSigFigDelta = 3,
            fractionDataType = Dyn.dataUtil.types.kNumerical+':'+Dyn.dataUtil.subtypes.kFraction;
		this.useFractionalRatios = xAttrib.getDataType(true)===fractionDataType && yAttrib.getDataType(true)===fractionDataType;
        this.slopeDigits = userPref + slopeSigFigDelta + Math.max( xAttrib.getNumericDecimals(), yAttrib.getNumericDecimals() );
        this.interceptDigits = userPref + interceptSigFigDelta + yAttrib.getNumericDecimals();
        this.rValueDigits = userPref + rSigFigDelta;
    }
    slopeString( slope, yAttr ) {
        // this is a ratio, no formatting for DateTime units
        if( this.useFractionalRatios )
            return yAttr.axisValueToString( slope, this.slopeDigits ) // show as a fraction (if X and Y are fractions)
        else
            return Dyn.mathUtil.roundToDecimalPlaces( slope, this.slopeDigits ); // else show as number with decimals
    }
    interceptNotAbs( intercept, yAttr) {
        return yAttr.axisValueToString( intercept, this.interceptDigits );
    }
    absInterceptString( intercept, yAttr ) {
        return yAttr.axisValueToString( Math.abs( intercept ), this.interceptDigits );
    }
    interceptPlusMinusString( intercept, yAttr ) {
        return (intercept >= 0 ? "+" : "-");
    }
    rValueString( rOrR2, yAttr ) {
        // this is a ratio, no formatting for DateTime units; data set may want to set a numeric base for attrs
		if( this.useFractionalRatios )
			return yAttr.axisValueToString( rOrR2, this.rValueDigits ) // show as a fraction (if X and Y are fractions)
		else
        	return Dyn.mathUtil.roundToDecimalPlaces( rOrR2, this.rValueDigits )
    }
}
Dyn.mathUtil.slopeInterceptDecimalPlaces = slopeInterceptDecimalPlaces;

Dyn.mathUtil.unitTest = function() {
    let unitTester = new Dyn.UnitTester( "Dyn.MathUtil.unitTest()" );
    function isEqualOrBothNaN( a, b ) {
        return( a===b || ( (!isFinite(a)) && (!isFinite(b))));
    }
    function testAndAssert( func, a, b, expectedResult ) {
        let result = Dyn.mathUtil[func]( a, b );
        unitTester.test( isEqualOrBothNaN( result, expectedResult), "Dyn.mathUtil.unitTest() failure: Dyn.mathUtil."+func+"( "+a+", "+b+" ) === "+result+", expected:"+ expectedResult );
        //if( a===b ) { console.log("Dyn.mathUtil.unitTest() passed: "+a+" === "+b)}
    }

    [   // (a ~< b) === result
        [1.0 - 1e-5,    1.0, true],
        [1.0 + 1e-5,    1.0, false],
        [1.0 - 1e-12,   1.0, false],
        [1.0 + 1e-12,   1.0, false],
    ].forEach( e => testAndAssert( 'isLessThan', e[0], e[1], e[2] ));

    [   // (a ~> b) === result
        [1.0 - 1e-5,    1.0, false],
        [1.0 + 1e-5,    1.0, true],
        [1.0 - 1e-12,   1.0, false],
        [1.0 + 1e-12,   1.0, false],
    ].forEach( e => testAndAssert( 'isGreaterThan', e[0], e[1], e[2] ));

    [   // (a ~>= b) === result
        [1.0 - 1e-5,    1.0, false],
        [1.0 + 1e-5,    1.0, true],
        [1.0 - 1e-12,   1.0, true],
        [1.0 + 1e-12,   1.0, true],
    ].forEach( e => testAndAssert( 'isGreaterThanOrEqual', e[0], e[1], e[2] ));

    [   // (a ~<= b) === result
        [1.0 - 1e-5,    1.0, true],
        [1.0 + 1e-5,    1.0, false],
        [1.0 - 1e-12,   1.0, true],
        [1.0 + 1e-12,   1.0, true],
    ].forEach( e => testAndAssert( 'isLessThanOrEqual', e[0], e[1], e[2] ));

    // test quartiles with both exclusive and inclusive methods
    function testAndAssert2( func, array, value, result, expectedResult ) {
        unitTester.test( result === expectedResult, "Dyn.mathUtil.unitTest() failure: Dyn.mathUtil."+func+"( "+array.toString()+" )["+value+"] === "+result+", expected: "+expectedResult );
        //if( a===b ) { console.log("Dyn.mathUtil.unitTest() passed: "+a+" === "+b)}
    }
    let a10 = [ 1,2,3,4,5,6,7,8,9,10 ],
        q10exclusive = Dyn.mathUtil.quartilesOfNumericArray( a10, false, false ),
        q10inclusive = Dyn.mathUtil.quartilesOfNumericArray( a10, false, true /* inclusive method for quartiles */ );

    testAndAssert2( 'quartilesOfNumericArray', q10exclusive, 0.25, q10exclusive.q1, 2.75 );
    testAndAssert2( 'quartilesOfNumericArray', q10exclusive, 0.75, q10exclusive.median, 5.5 );
    testAndAssert2( 'quartilesOfNumericArray', q10exclusive, 0.5, q10exclusive.q3, 8.25 );

    testAndAssert2( 'quartilesOfNumericArray', q10inclusive, 0.25, q10inclusive.q1, 3.25 );
    testAndAssert2( 'quartilesOfNumericArray', q10inclusive, 0.5, q10inclusive.median, 5.5 );
    testAndAssert2( 'quartilesOfNumericArray', q10inclusive, 0.75, q10inclusive.q3, 7.75 );

    testAndAssert( 'sumOfNumericArray', [1,2,2,3.5], undefined,8.5 );
    testAndAssert( 'meanOfNumericArray', [1,2,2,3.5], undefined,8.5/4 );
    testAndAssert( 'stDevOfNumericArray', [1,2,2,3.5], true /* pop. st. dev */,0.8926785535678563 );
    testAndAssert( 'stDevOfNumericArray', [1,2,2,3.5], false /* sample. st. dev */,1.0307764064044151 );

    testAndAssert( 'meanOfNumericArray', [], undefined, NaN );
    testAndAssert( 'stDevOfNumericArray', [], true, NaN );
    testAndAssert( 'stdErrorOfNumericArray', [], undefined, NaN );

    let height = [150.5, 170, 160, 161, 170.5]; // https://www.radford.edu/~biol-web/stats/standarderrorcalc.pdf
    testAndAssert( 'meanOfNumericArray', height, undefined, 162.4 );
    testAndAssert( 'varianceOfNumericArray', height, false,  68.175 );
    testAndAssert( 'stDevOfNumericArray', height, false, 8.256815366713731 );
    testAndAssert( 'stdErrorOfNumericArray', height, undefined, 3.692560087527351 );

    let cm1 = new Dyn.mathUtil.CoordMap( 123.45, 578.9, -234, 0, 4 ),
        cm2 = cm1.clone();
    Object.keys(cm1).forEach( key => Dyn.assert( cm1[key]===cm2[key])); // clone should have identical values

    unitTester.report();
    return true;
};

// enabled for debugging only
Dyn.debugMathUtil_UnitTestHasRun = ( Dyn.debugging ? Dyn.mathUtil.unitTest() : false );

// Unit test cases for checking mathUtil routines
// To run QUnit use: http://127.0.0.1:8000/plot-testing/
(function unitTests(){
    "use strict";

    if (typeof QUnit !== "undefined"){
        // string values
        QUnit.test( "Dyn.mathUtil: test math utilities", function( assert ) {

            assert.ok( Dyn.mathUtil.notCloseToZero( 1 ) === true, "ok: Dyn.mathUtil.notCloseToZero( 1 ) === true");
            assert.ok( Dyn.mathUtil.notCloseToZero( 0 ) === false, "ok: Dyn.mathUtil.notCloseToZero( 0 ) === false");

            assert.ok( Dyn.mathUtil.numericStringWithCommas( 0 )==='0', "ok: Dyn.mathUtil.numericStringWithCommas( 0 )==='0'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( 1e7 )==="10,000,000", "ok: Dyn.mathUtil.numericStringWithCommas( 1e7 )==='10,000,000'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( undefined )==='undefined', "ok: Dyn.mathUtil.numericStringWithCommas( undefined )==='undefined'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( -1234567.89 )==='-1,234,567.89', "ok: Dyn.mathUtil.numericStringWithCommas( -1234567.89 )==='-1,234,567.89'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( 1234567.8901234567890 )==='1,234,567.8901234567', "ok: Dyn.mathUtil.numericStringWithCommas( 1234567.8901234567890 )==='1,234,567.8901234567'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( 'I have 12345 rocks') === 'I have 12,345 rocks', "ok: Dyn.mathUtil.numericStringWithCommas( 'I have 12345 rocks') === 'I have 12,345 rocks'" );
            assert.ok( Dyn.mathUtil.numericStringWithCommas( 1999 )==='1,999', "ok: Dyn.mathUtil.numericStringWithCommas( 1999 )==='1,999'");
            assert.ok( Dyn.mathUtil.numericStringWithCommas( "1999-07-27" )==='1,999-07-27', "ok: Dyn.mathUtil.numericStringWithCommas( '1999-07-27' )==='1,999-07-27'");

            assert.ok( Dyn.mathUtil.roundToPrecision( 20.1237, 3 )===20.1,"ok: roundToPrecision( 20.1237, 3 )===20.1");
            assert.ok( Dyn.mathUtil.roundToPrecision( 20.1237, 5 )===20.124,"ok: roundToPrecision( 20.1237, 5 )===20.124");

            assert.ok( Dyn.mathUtil.roundToDecimalPlaces( 20.1237, 3 )===20.124,"ok: roundToDecimalPlaces( 20.1237, 3 )===20.124");
            assert.ok( Dyn.mathUtil.roundToDecimalPlaces( 20.1237, 0 )===20,"ok: roundToDecimalPlaces( 20.1237, 3 )===20");
        });
    }
})();
}
